import {mapObject} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {assert} from 'Asserts';
import {TestOrderStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {BLUE_LIGHT, GRAY_DARK, GRAY_SEMIDARK, GREEN, RED} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {capitalize, constantCaseToWords} from 'util/NameUtil';

/**
 * Mapping from test order status to icon & display styling.
 */
export const TEST_ORDER_STATUS_ICONS: Record<
  TestOrderStatus,
  {icon: BenchlingIconName; style: {color: string}}
> = {
  [TestOrderStatus.PENDING]: {
    icon: 'request-pending',
    style: {color: GRAY_DARK},
  },
  [TestOrderStatus.INVALID]: {
    icon: 'circle-xmark',
    style: {color: RED},
  },
  [TestOrderStatus.IN_PROGRESS]: {
    icon: 'request-in-progress',
    style: {color: BLUE_LIGHT},
  },
  [TestOrderStatus.COMPLETED]: {
    icon: 'check-circle',
    style: {color: GREEN},
  },
  [TestOrderStatus.CANCELLED]: {
    icon: 'cancelled',
    style: {color: RED},
  },
  [TestOrderStatus.BLOCKED]: {
    icon: 'cancelled',
    style: {color: GRAY_SEMIDARK},
  },
};

/**
 * Get test order statuses in logical order, e.g. for displaying dropdown options.
 */
export function getOrderedTestOrderStatuses(): Array<TestOrderStatus> {
  const orderedStatuses = [
    TestOrderStatus.PENDING,
    TestOrderStatus.BLOCKED,
    TestOrderStatus.IN_PROGRESS,
    TestOrderStatus.CANCELLED,
    TestOrderStatus.INVALID,
    TestOrderStatus.COMPLETED,
  ];
  assert(
    orderedStatuses.length === Object.keys(TestOrderStatus).length,
    'Expected all test order statuses to be included in ordering'
  );
  return orderedStatuses;
}

/**
 * Get readable display label for test order status.
 */
export function getTestOrderStatusDisplayText(status: TestOrderStatus): string {
  return capitalize(constantCaseToWords(status));
}

/**
 * Test order status display component; renders friendly label with icon.
 */
export function TestOrderStatusDisplay(props: TestOrderStatusDisplayProps): JSX.Element {
  return (
    <div className={props.className}>
      <TestOrderStatusIcon status={props.status} />
      <span>{getTestOrderStatusDisplayText(props.status)}</span>
    </div>
  );
}

interface TestOrderStatusDisplayProps {
  status: TestOrderStatus;
  className?: string;
}

/**
 * Test order status icon component.
 */
function TestOrderStatusIcon({status}: {status: TestOrderStatus}): JSX.Element {
  return (
    <BenchlingIcon
      icon={TEST_ORDER_STATUS_ICONS[status].icon}
      className={classNames(css(styles.statusIcon), css(TEST_ORDER_STATUS_ICON_STYLES[status]))}
    />
  );
}

const TEST_ORDER_STATUS_ICON_STYLES = StyleSheet.create(
  mapObject(TEST_ORDER_STATUS_ICONS, (info) => ({...info.style}))
);

const styles = StyleSheet.create({
  statusIcon: {
    marginRight: 8,
  },
});
